import { FC, ReactNode } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { ORG_PAGE_SOURCE, SOURCE_QUERY_PARAM_KEY } from "@simplyk/common";

import { CampaignCardMedia } from "./CampaignCardMedia/CampaignCardMedia";
import { CampaignCategory, getCampaignCategoryDisplay } from "./helpers/campaignCategory";

import { appendQueryParams } from "@/common/helpers/url";
import { useDynamicThemeAwareVariables } from "@/common/hooks/useDynamicThemeAwareVariables";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormChip } from "@/forms-design-system/FormChip";
import { FormTypography } from "@/forms-design-system/FormTypography";

const CARD_SHADOW = "0 8px 16px 0 rgba(0, 0, 0, 0.12)";

const MEDIA_SIZES = {
  event: { xs: 12, sm: 17 },
  supportForm: { xs: 20, sm: 23 },
} as const;

interface CampaignCardProps {
  variant: "event" | "supportForm";
  title: string;
  url: string;
  category: CampaignCategory;
  bannerUrl: string | null;
  bannerVideoUrl: string | null;
  isSoldOut: boolean;
  isSalesClosed: boolean;
  hoverLabel?: string;
  onClick?: () => void;
  slots: {
    details: ReactNode;
  };
}

export const CampaignCard: FC<CampaignCardProps> = ({
  variant,
  title,
  url,
  category,
  bannerUrl,
  bannerVideoUrl,
  isSoldOut,
  isSalesClosed,
  hoverLabel: hoverLabelOverride,
  onClick,
  slots,
}) => {
  const { t } = useTranslate();
  const { icon, getLabel, getHoverLabel } = getCampaignCategoryDisplay(category);
  const hoverLabel = hoverLabelOverride ?? getHoverLabel(t);
  const { backgroundColor, borderColor, hoverBorderColor } = useDynamicThemeAwareVariables(
    ({ formsPalette, mode }) => ({
      backgroundColor: mode === "light" ? formsPalette.surface.quiet : formsPalette.surface.moderate,
      borderColor: mode === "light" ? formsPalette.border.moderate : formsPalette.border.supershy,
      hoverBorderColor: mode === "light" ? formsPalette.border.intense : formsPalette.border.moderate,
    })
  );
  const isEvent = variant === "event";
  const mediaSize = MEDIA_SIZES[variant];

  return (
    <Box
      component="a"
      href={appendQueryParams(url, { [SOURCE_QUERY_PARAM_KEY]: ORG_PAGE_SOURCE })}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      aria-label={t("common", "a11y.opensInNewTab", { label: `${hoverLabel}: ${title}` })}
      data-test="org-page-campaign-card"
      sx={(theme) => ({
        display: "flex",
        alignItems: isEvent ? { xs: "flex-start", sm: "stretch" } : "stretch",
        gap: isEvent ? { xs: 2, sm: 0 } : { xs: 2, sm: 3 },
        padding: isEvent ? { xs: 2, sm: 0 } : 0,
        paddingRight: isEvent ? undefined : { xs: 2, sm: 3 },
        flexShrink: 0,
        position: "relative",
        boxSizing: "border-box",
        overflow: "hidden",
        borderRadius: theme.radius(1),
        backgroundColor,
        boxShadow: CARD_SHADOW,
        color: "inherit",
        textDecoration: "none",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          zIndex: 1,
          border: `1px solid ${borderColor}`,
          borderRadius: "inherit",
          pointerEvents: "none",
          transition: "border-color 200ms ease-out",
        },
        "&:focus-visible": {
          outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
          outlineOffset: 0,
        },
        "@media (hover: hover)": {
          "&:hover": {
            "&::after": { borderColor: hoverBorderColor },
            "& [data-role='campaign-card-hover-overlay']": { opacity: 1 },
          },
        },
      })}
    >
      <Box
        sx={(theme) => ({
          flexShrink: 0,
          width: { xs: theme.spacing(mediaSize.xs), sm: theme.spacing(mediaSize.sm) },
          minHeight: { xs: theme.spacing(mediaSize.xs), sm: theme.spacing(mediaSize.sm) },
          height: isEvent ? { xs: theme.spacing(mediaSize.xs), sm: "auto" } : "auto",
          borderRadius: isEvent ? { xs: theme.radius(1), sm: 0 } : 0,
          overflow: "hidden",
        })}
      >
        <CampaignCardMedia
          bannerUrl={bannerUrl}
          bannerVideoUrl={bannerVideoUrl}
          categoryIcon={icon}
          hoverLabel={hoverLabel}
        />
      </Box>
      <Stack
        justifyContent="center"
        gap={isEvent ? { xs: 1, sm: 1.5 } : 1}
        sx={{
          flex: 1,
          minWidth: 0,
          padding: isEvent ? { xs: 0, sm: 3 } : 0,
          paddingBlock: isEvent ? undefined : { xs: 2, sm: 3 },
        }}
      >
        <Stack gap={1}>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            <FormChip variant="filled" vibe="emphasis" icon={icon} label={getLabel(t)} />
            {isSoldOut || isSalesClosed ? (
              <FormChip
                variant="outlined"
                vibe="danger"
                label={t("organization", isSoldOut ? "campaignCard.status.soldOut" : "campaignCard.status.salesClosed")}
              />
            ) : null}
          </Stack>
          <FormTypography
            variant="h5"
            vibe="intense"
            sx={{
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflowWrap: "anywhere",
            }}
          >
            {title}
          </FormTypography>
        </Stack>
        {slots.details}
      </Stack>
    </Box>
  );
};
